import { FC, ReactNode, useState, useEffect } from "react";
import moment from "moment";
import { NotificationManager } from 'react-notifications';
import Modal from "../../components/shared/Modal/Modal";
import Field from "../../components/shared/Field/Field";
import DatePicker from "../../components/shared/DatePicker/DatePicker";
import EmailPreview from "../../components/shared/EmailPreview/EmailPreview";
import { useTranslation } from "react-i18next";

type Props = {
  show: boolean,
  setShow: (value: boolean) => void,
  defaultDate: Date,
  // Whatever it resolves with is handed to `shouldNotifySuccess` to judge the outcome.
  onSave: ({ date, emailText }: { date: Date, emailText: string }) => Promise<unknown>,
  // `version` is omitted when one send spans several versions (bulk), where a single version
  // number would be wrong — the line is then hidden rather than showing a misleading one.
  data: { areaName: string, projectName: string, version?: number },
  // Optional line under the intro, e.g. how many suppliers a bulk extension will reach.
  recipientNote?: ReactNode,
  // Blocks Skicka, e.g. when the selection exceeds what one send may reach.
  disableSubmit?: boolean,
  // Lets the caller turn a specific failure into a specific message. Returning undefined (or
  // omitting the prop) falls back to the generic "try again" text.
  getErrorMessage?: (error: any) => string | undefined,
  // Lets the caller judge whether a save that resolved is actually a success. A bulk send answers
  // 200 even when it extended nobody, and announcing "Sparat" over the caller's own failure notices
  // would contradict them. Omitted — the single-supplier case, where resolving means it worked —
  // keeps every resolved save a success.
  shouldNotifySuccess?: (result: unknown) => boolean,
}

const DateExtensionModal: FC<Props> = ({
  show,
  setShow,
  defaultDate,
  onSave,
  data,
  recipientNote,
  disableSubmit,
  getErrorMessage,
  shouldNotifySuccess,
}) => {
  const [t] = useTranslation('projects', { keyPrefix: 'dateExtension' });
  const [date, setDate] = useState(defaultDate || new Date());
  const [emailText, setEmailText] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // The modal stays mounted (only `show` toggles), so re-seed the picker with the default date
  // (today) every time it is opened rather than keeping the previously picked value.
  useEffect(() => {
    if (show) setDate(defaultDate || new Date());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const { areaName, projectName, version } = data;

  const onSubmit = async () => {
    setIsSaving(true);
    try {
      const result = await onSave({ date: moment(date).endOf('day').utc().toDate(), emailText });
      // A resolved save is a success unless the caller says otherwise — see shouldNotifySuccess.
      if (shouldNotifySuccess?.(result) ?? true) {
        NotificationManager.success(
          t('notification.success', 'Uppdaterade utgångsdatumet'),
          t('notification.successTitle', 'Sparat'),
        );
      }
    } catch (e) {
      console.error('Failed to change the bid deadline', e);
      NotificationManager.error(
        getErrorMessage?.(e) ?? t('notification.error', 'Det gick inte att ändra anbudstiden, försök igen'),
        t('notification.errorTitle', 'Fel'),
      );
    }
    setIsSaving(false);
  };

  return (
    <Modal
      title={t('heading', 'Ändra anbudstid')}
      show={show}
      setShow={setShow}
      buttonInfo={{ label: 'Skicka', action: onSubmit, loaderShow: isSaving, disabled: disableSubmit }}
      hasCloseButton
    >
      <div className='dateExtensionModal'>
        {/* Who this reaches comes first — it is the thing to check before picking a date. */}
        {recipientNote && <div className='dateExtensionModalRecipients'>{recipientNote}</div>}
        <p>{t('intro', 'Välj nytt datum och skriv ett meddelande nedan för att ändra utgångsdatum. Följande e-postmeddelande skickas till leverantörens kontaktpersoner:')}<br/><br/></p>

        <p className='dateExtensionModalDateContainer'>
          <span>{t('emailTemplate.info.label.date', 'Utgångsdatum')}:</span>
          <DatePicker
            minDate={new Date()}
            selected={date}
            onChange={setDate}
          />
        </p>

        <p><strong style={{ display: 'block', paddingBottom: '5px' }}>{t('emailTemplate.message.title', 'Meddelande från avsändaren:')}</strong></p>
        <Field multiline value={emailText} onChange={setEmailText} /><br/>

        <EmailPreview
          type="dateChange"
          params={{
            areaName,
            projectName,
            versionNo: typeof version === 'number' ? version : null,
            expiryDate: moment(date).format('YYYY-MM-DD'),
            emailText,
          }}
        />
      </div>
    </Modal>
  )
}

export default DateExtensionModal;
